// @ts-nocheck
/* Store Custom Code
import {
    ApiParameter,
    doesNotHaveExpand,
    get,
    HasPagingParameters,
    patch,
} from "@insite/client-framework/Services/ApiService";
*/
// import { CreditCardDto, InvoiceCollectionModel, InvoiceModel } from "@insite/client-framework/Types/ApiModels"; // Store Custom Code

/* Store Custom Code - Start */
import {
    ApiParameter,
    doesNotHaveExpand,
    get,
    HasPagingParameters,
    patch,
    post,
} from "@insite/client-framework/Services/ApiService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { CreditCardDto, InvoiceCollectionModel, InvoiceModel } from "@insite/client-framework/Types/ApiModels";
import { InvoicePaymentSuccessModel } from "@store/Framework/Types/ApiModels";
import getLocalizedCurrency from "@store/Modules/client-framework/src/Common/Utilities/getLocalizedCurrency";

export interface ExtendedInvoicesApiParameter extends GetInvoicesApiParameter {
    amount?: string;
    amountType?: string;
}

export interface PayInvoicesApiParameter {
    invoices: string[];
    paymentProfileId: string;
    creditCard?: CreditCardDto;
    amount: number;
}
/* Store Custom Code - End */

export interface GetInvoicesApiParameter extends ApiParameter, HasPagingParameters {
    invoiceNumber?: string;
    orderNumber?: string;
    poNumber?: string;
    search?: string;
    status?: string[];
    customerSequence?: string;
    fromDate?: string;
    toDate?: string;
    showOpenOnly?: boolean;
}

export interface GetInvoiceApiParameter extends ApiParameter {
    invoiceNumber: string;
    expand?: "invoiceLines"[];
    additionalExpands?: string[];
    sTEmail?: string;
    sTPostalCode?: string;
}

export interface UpdateInvoiceApiParameter extends ApiParameter {
    invoice: InvoiceModel;
}

const invoicesUrl = "/api/v1/custom/invoices"; // Store Custom Code

export async function getInvoices(
    parameter: GetInvoicesApiParameter,
    state?: ApplicationState, // Store Custom Code
) {
    const invoices = await get<InvoiceCollectionModel>(invoicesUrl, parameter);
    invoices.invoices?.forEach(o => cleanInvoice(o, undefined, state /* Store Custom Code */));
    return invoices;
}

export async function getInvoice(
    parameter: GetInvoiceApiParameter,
    state?: ApplicationState, // Store Custom Code
) {
    const { invoiceNumber, ...newParameter } = parameter;
    const invoice = await get<InvoiceModel>(`${invoicesUrl}/${invoiceNumber.replace("-", "/")}`, newParameter); // Store Custom Code
    cleanInvoice(invoice, parameter, state /* Store Custom Code */);
    return invoice;
}

export function updateInvoice(parameter: UpdateInvoiceApiParameter) {
    const invoice = parameter.invoice;
    const invoiceNumber = invoice.invoiceNumber;
    return patch<InvoiceModel>(`${invoicesUrl}/${invoiceNumber}`, invoice);
}

function cleanInvoice(
    invoice: InvoiceModel,
    parameter?: { expand?: string[]; additionalExpands?: string[] },
    state?: ApplicationState, // Store Custom Code
) {
    /* Store Custom Code - Start */
    const language = state?.context.session.language;
    const currencySymbol = state?.context.session.currency?.currencySymbol;

    const toCurrency = (amount: number | undefined | null) => {
        if (typeof amount !== "number" || !language || !currencySymbol) {
            return amount?.toFixed(2) || "";
        }

        return getLocalizedCurrency({ amount, language, currencySymbol });
    };

    invoice.invoiceLines =
        invoice.invoiceLines ||
        invoice.lines?.map(line => {
            const lineNumber = typeof line.lineNumber === "number" ? `${line.lineNumber}` : line.lineNumber; // Custom API provides a number instead of a string
            const productERPNumber = line.productERPNumber || line.productNumber || "";
            const productName = line.productName || line.productDescription || "";

            return {
                ...line,
                lineNumber,
                productERPNumber,
                productName,
                shortDescription: productName,
                unitPriceDisplay: line.unitPriceDisplay || toCurrency(line.unitPrice),
                lineTotalDisplay: line.lineTotalDisplay || toCurrency(line.lineTotal),
                qtyInvoiced: typeof line.qtyInvoiced === "number" ? line.qtyInvoiced : line.orderedQuantity,
                discountAmount: typeof line.discountAmount === "number" ? line.discountAmount : line.discount,
                discountAmountDisplay: line.discountAmountDisplay || toCurrency(line.discount),
                properties: line.properties || {},
                id: line.id || `${productERPNumber}-${lineNumber}`,
            };
        }) ||
        null;

    invoice.btCompanyName = invoice.btCompanyName || invoice.billTo?.companyName || "";
    invoice.btAddress1 = invoice.btAddress1 || invoice.billTo?.address1 || "";
    invoice.btAddress2 = invoice.btAddress2 || invoice.billTo?.address2 || "";
    invoice.billToCity = invoice.billToCity || invoice.billTo?.city || "";
    invoice.billToState = invoice.billToState || invoice.billTo?.state || "";
    invoice.billToPostalCode = invoice.billToPostalCode || invoice.billTo?.postalCode || "";
    invoice.btCountry = invoice.btCountry || invoice.billTo?.country || "";
    invoice.stCompanyName = invoice.stCompanyName || invoice.shipTo?.companyName || "";
    invoice.stAddress1 = invoice.stAddress1 || invoice.shipTo?.address1 || "";
    invoice.stAddress2 = invoice.stAddress2 || invoice.shipTo?.address2 || "";
    invoice.shipToCity = invoice.shipToCity || invoice.shipTo?.city || "";
    invoice.shipToState = invoice.shipToState || invoice.shipTo?.state || "";
    invoice.shipToPostalCode = invoice.shipToPostalCode || invoice.shipTo?.postalCode || "";
    invoice.stCountry = invoice.stCountry || invoice.shipTo?.country || "";
    invoice.shipViaDescription = invoice.shipViaDescription || invoice.shipVia || "";
    invoice.dueDate = typeof invoice.dueDate === "string" ? new Date(invoice.dueDate) : invoice.dueDate;
    invoice.poDate = typeof invoice.poDate === "string" ? new Date(invoice.poDate) : invoice.poDate;
    invoice.invoiceDate =
        typeof invoice.invoicedDate === "string" ? new Date(invoice.invoicedDate) : invoice.invoiceDate;
    invoice.shipDate = typeof invoice.shipDate === "string" ? new Date(invoice.shipDate) : invoice.shipDate;
    invoice.productTotal = typeof invoice.productTotal === "number" ? invoice.productTotal : invoice.subTotal || 0;
    invoice.productTotalDisplay = invoice.productTotalDisplay || toCurrency(invoice.productTotal);
    invoice.taxAmount = typeof invoice.taxAmount === "number" ? invoice.taxAmount : invoice.taxes || 0;
    invoice.taxAmountDisplay = invoice.taxAmountDisplay || toCurrency(invoice.taxAmount);
    invoice.invoiceTotal =
        typeof invoice.invoiceTotal === "number" ? invoice.invoiceTotal : invoice.total || invoice.invoicedAmount || 0;
    invoice.invoiceTotalDisplay = invoice.invoiceTotalDisplay || toCurrency(invoice.invoiceTotal);
    invoice.discountAmountDisplay = toCurrency(invoice.discountAmount);
    invoice.provincialTaxAmountDisplay = toCurrency(invoice.provincialTaxAmount);
    invoice.countryTaxAmountDisplay = toCurrency(invoice.countryTaxAmount);
    invoice.currentBalance = invoice.dueAmount || 0;
    invoice.currentBalanceDisplay = toCurrency(invoice.currentBalance);
    invoice.paidAmountDisplay = toCurrency(invoice.paidAmount);

    if (typeof invoice.otherCharges === "object") {
        invoice.customOtherCharges = (invoice.otherCharges as any[]).map(charge => ({
            ...charge,
            amountDisplay: toCurrency(charge.amount),
        }));
        invoice.otherCharges = 0;
    }

    const invoiceDateTimestamp = new Date(invoice.invoiceDate).getTime();
    invoice.id = `${invoice.invoiceNumber}-${invoice.invoiceSequence}-${invoiceDateTimestamp}`;
    invoice.paymentId = `${invoice.invoiceUniqueNumber}:${invoiceDateTimestamp}`;
    /* Store Custom Code - End */
    if (doesNotHaveExpand(parameter, "invoiceLines")) {
        invoice.invoiceLines = null;
    }
}

/* Store Custom Code - Start */
export async function payInvoices(parameter: PayInvoicesApiParameter) {
    const result = await post<PayInvoicesApiParameter, InvoicePaymentSuccessModel>(`${invoicesUrl}/pay`, parameter);

    return result;
}
/* Store Custom Code - End */
